import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 8 10 8a18 18 0 0 1-2.16 3.19M6.7 6.7A18 18 0 0 0 2 12s3.5 7 10 7a9 9 0 0 0 5.3-1.7" />
    <path d="M3 3l18 18" />
  </svg>
);

const Register = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    authMode,
    completeLocalAuth,
    completePasswordAuth,
    registerUser,
    requestWhatsAppCode
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // Watch password for confirmation validation
  const password = watch('password', '');
  
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Format the date for approved_terms_date
      const now = new Date().toISOString();
      
      // Register the user
      await registerUser({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
        gender: data.gender,
        approved_terms_date: now,
        approved_terms: true
      });

      if (authMode === 'password') {
        await completePasswordAuth({
          email: data.email,
          password: data.password
        });
        navigate('/upload-cv');
        return;
      }

      if (authMode === 'local') {
        await completeLocalAuth();
        navigate('/upload-cv');
        return;
      }
      
      // Request WhatsApp verification code
      try {
        await requestWhatsAppCode();
      } catch (whatsappErr) {
        console.error('WhatsApp verification error:', whatsappErr);
        // Continue even if WhatsApp verification fails
      }
      
      // Navigate to WhatsApp verification page
      navigate('/verify-whatsapp');
    } catch (err) {
      if (err.response?.data?.error === 'already_registered') {
        navigate('/login', {
          state: {
            email: data.email,
            message: err.response?.data?.error_message || 'כבר קיים עבורך חשבון במערכת. נא להתחבר.'
          }
        });
        return;
      }

      if (err.response?.data?.error_message) {
        setError(err.response.data.error_message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בהרשמה. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="הרשמה"
      altText="או"
      altLinkText="התחברות למערכת"
      altLinkTo="/login"
      LinkComponent={Link}
    >
      {error && (
        <div className="mb-6 rounded-xl border border-red-400 bg-red-100 px-4 py-3 text-red-700" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <form className="space-y-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-3">
          <div>
            <label htmlFor="firstName" className="auth-label">שם פרטי</label>
            <input
              id="firstName"
              type="text"
              {...register('firstName', { required: 'שם פרטי הוא שדה חובה' })}
              className="auth-input"
              placeholder="שם פרטי"
            />
            {errors.firstName && (
              <p className="text-red-500 text-[0.65rem] mt-1">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="auth-label">שם משפחה</label>
            <input
              id="lastName"
              type="text"
              {...register('lastName', { required: 'שם משפחה הוא שדה חובה' })}
              className="auth-input"
              placeholder="שם משפחה"
            />
            {errors.lastName && (
              <p className="text-red-500 text-[0.65rem] mt-1">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="auth-label">אימייל</label>
          <input
            id="email"
            type="email"
            {...register('email', {
              required: 'אימייל הוא שדה חובה',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'אנא הזן כתובת אימייל תקינה'
              }
            })}
            className="auth-input"
            placeholder="הזן את כתובת האימייל שלך"
            dir="ltr"
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label htmlFor="phone" className="auth-label">טלפון</label>
            <input
              id="phone"
              type="tel"
              {...register('phone', {
                required: 'טלפון הוא שדה חובה',
                pattern: {
                  value: /^[0-9+\-\s()]*$/,
                  message: 'אנא הזן מספר טלפון תקין'
                }
              })}
              className="auth-input"
              placeholder="טלפון"
              dir="ltr"
            />
            {errors.phone && (
              <p className="text-red-500 text-[0.65rem] mt-1">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="gender" className="auth-label">מגדר</label>
            <select
              id="gender"
              {...register('gender')}
              className="auth-input"
              defaultValue=""
            >
              <option value="">בחר מגדר</option>
              <option value="male">זכר</option>
              <option value="female">נקבה</option>
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label htmlFor="password" className="auth-label">סיסמה</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                {...register('password', {
                  required: 'סיסמה היא שדה חובה',
                  minLength: {
                    value: 6,
                    message: 'לפחות 6 תווים'
                  }
                })}
                className="auth-input pl-9"
                placeholder="סיסמה"
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 left-2 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-[0.65rem] mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="auth-label">אימות סיסמה</label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                {...register('confirmPassword', {
                  required: 'אימות סיסמה הוא שדה חובה',
                  validate: value => value === password || 'הסיסמאות אינן תואמות'
                })}
                className="auth-input pl-9"
                placeholder="אימות"
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 left-2 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={showConfirm ? 'הסתר סיסמה' : 'הצג סיסמה'}
              >
                {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-[0.65rem] mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>

        <button type="submit" disabled={isLoading} className="btn-orange">
          {isLoading ? 'מבצע הרשמה...' : 'הרשמה'}
        </button>

        <p className="text-center text-xs text-gray-600">
          בלחיצה על הרשמה, אני מאשר/ת את{' '}
          <a
            href="https://www.rebase.co.il/terms_of_use"
            className="text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            תנאי השימוש ומדיניות הפרטיות
          </a>
        </p>
      </form>
    </AuthLayout>
  );
};

export default Register;
